import { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import formatDate from "../components/FormatDate";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import Toast from "../components/Toast";
import UploadFileModal from "../components/UploadFileModal";

export default function Files() {
  const [files, setFiles] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("filename");
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [openModalFile, setOpenModalFile] = useState(false);

  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedFiles = [...files].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "assignedToAll") {
      aValue = a.assignedToAll
        ? "All Users"
        : a.assignedUsers?.map((user) => user.name).join(", ") || "";
      bValue = b.assignedToAll
        ? "All Users"
        : b.assignedUsers?.map((user) => user.name).join(", ") || "";
    }

    aValue = aValue ?? "";
    bValue = bValue ?? "";

    if (orderBy === "uploaded_at") {
      aValue = aValue ? new Date(aValue) : new Date(0);
      bValue = bValue ? new Date(bValue) : new Date(0);
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });

  const fetchFiles = () => {
    const endpoint =
      role === "admin"
        ? `${API_BASE_URL}/api/files`
        : `${API_BASE_URL}/api/user/files`;

    fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setFiles(data))
      .catch((err) => console.error("Failed to fetch files:", err));
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/files/${selectedFileId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        fetchFiles();
      } else {
        alert(data.error || "Failed to delete.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Unexpected error occurred.");
    } finally {
      setModalOpen(false);
    }
  };

  const handleFileUpload = async (formData) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
        credentials: "include",
      });

      const data = await res.json();

      if (res.ok) {
        showToast("File uploaded successfully!", "success");
        fetchFiles();
      } else {
        showToast(data.error || "Failed to upload.", "error");
      }
    } catch (err) {
      console.error("Upload error:", err);
      showToast("Upload failed.", "error");
    }
  };

  const colorPalette = [
    "#F29E3D", // orange
    "#4CAF50", // green
    "#2196F3", // blue
    "#9C27B0", // purple
    "#E91E63", // pink
    "#FF5722", // deep orange
    "#009688", // teal
  ];

  const getColorForName = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colorPalette.length);
    return colorPalette[index];
  };

  return (
    <>
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, visible: false })}
      />

      <Box className="users-page">
        <Box
          className="header"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={500}>
            Uploaded Files
          </Typography>

          {role === "admin" && (
            <div className="add-button" onClick={() => setOpenModalFile(true)}>
              <CloudUploadOutlinedIcon />
              Upload File
            </div>
          )}
        </Box>

        <UploadFileModal
          open={openModalFile}
          onClose={() => setOpenModalFile(false)}
          onSubmit={handleFileUpload}
        />

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderBy === "filename" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "filename"}
                    direction={orderBy === "filename" ? order : "asc"}
                    onClick={() => handleSort("filename")}
                  >
                    File Name
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sortDirection={orderBy === "uploaded_by" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "uploaded_by"}
                    direction={orderBy === "uploaded_by" ? order : "asc"}
                    onClick={() => handleSort("uploaded_by")}
                  >
                    Uploaded By
                  </TableSortLabel>
                </TableCell>

                {role === "admin" && (
                  <TableCell
                    sortDirection={orderBy === "assignedToAll" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "assignedToAll"}
                      direction={orderBy === "assignedToAll" ? order : "asc"}
                      onClick={() => handleSort("assignedToAll")}
                    >
                      Assigned To
                    </TableSortLabel>
                  </TableCell>
                )}

                <TableCell
                  sortDirection={orderBy === "uploaded_at" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "uploaded_at"}
                    direction={orderBy === "uploaded_at" ? order : "asc"}
                    onClick={() => handleSort("uploaded_at")}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>

                {role === "admin" && (
                  <TableCell style={{ textTransform: "capitalize" }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedFiles.length > 0 ? (
                sortedFiles.map((file) => (
                  <TableRow hover key={file.id ?? file.filename}>
                    <TableCell>
                      <a
                        href={`${API_BASE_URL}/uploads/${file.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.filename}
                      </a>
                    </TableCell>

                    <TableCell>{file.uploaded_by || "N/A"}</TableCell>

                    {role === "admin" && (
                      <TableCell>
                        {file.assignedToAll ? (
                          <span
                            style={{
                              background: "#F29E3D",
                              color: "#fff",
                              borderRadius: 30,
                              padding: "1px 10px",
                              fontSize: "11px",
                              display: "inline-block",
                            }}
                          >
                            All Users
                          </span>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {file.assignedUsers?.map((user) => {
                              const color = getColorForName(user.name || "");
                              return (
                                <span
                                  key={user.id ?? user.name}
                                  style={{
                                    backgroundColor: color,
                                    color: "#fff",
                                    borderRadius: 30,
                                    padding: "1px 10px",
                                    fontSize: "11px",
                                    display: "inline-block",
                                  }}
                                >
                                  {user.name}
                                </span>
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>
                    )}

                    <TableCell>
                      {file.uploaded_at ? formatDate(file.uploaded_at) : ""}
                    </TableCell>

                    {role === "admin" && (
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setSelectedFileId(file.id);
                            setModalOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={role === "admin" ? 5 : 4} align="center">
                    No files uploaded yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={confirmDelete}
          itemName="file"
        />
      </Box>
    </>
  );
}
